import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../App";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "./ui/sheet";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { ScrollArea } from "./ui/scroll-area";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  HelpCircle, Search, ExternalLink, ChevronRight, ArrowLeft, Zap, BookOpen, Loader2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function detectPhase(pathname) {
  if (pathname.includes("/define")) return "define";
  if (pathname.includes("/measure")) return "measure";
  if (pathname.includes("/analyze")) return "analyze";
  if (pathname.includes("/improve")) return "improve";
  if (pathname.includes("/control")) return "control";
  if (pathname.includes("/closure")) return "closure";
  if (pathname.includes("/project/")) return "define";
  return null;
}

export default function HelpButton() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [articles, setArticles] = useState([]);
  const [contextArticles, setContextArticles] = useState([]);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [aiQuestion, setAiQuestion] = useState("");
  const [aiAnswer, setAiAnswer] = useState("");
  const [aiAsking, setAiAsking] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const currentPhase = detectPhase(location.pathname);

  useEffect(() => {
    if (!open) return;
    fetch(`${API}/help/articles`)
      .then(r => r.json())
      .then(d => setArticles(d.articles || []));
  }, [open]);

  useEffect(() => {
    if (!open || !currentPhase) { setContextArticles([]); return; }
    fetch(`${API}/help/articles?phase=${currentPhase}`)
      .then(r => r.json())
      .then(d => setContextArticles(d.articles || []));
  }, [open, currentPhase]);

  const doSearch = useCallback(async (q) => {
    if (!q.trim()) { setSearchResults(null); return; }
    const res = await fetch(`${API}/help/search?q=${encodeURIComponent(q)}`);
    const d = await res.json();
    setSearchResults(d.results || []);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => doSearch(searchQuery), 300);
    return () => clearTimeout(t);
  }, [searchQuery, doSearch]);

  const loadArticle = async (slug) => {
    const res = await fetch(`${API}/help/articles/${slug}`);
    const d = await res.json();
    setSelectedArticle(d.article);
  };

  const askAi = async () => {
    if (!aiQuestion.trim()) return;
    setAiAsking(true);
    setAiAnswer("");
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/help/ask-ai`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ question: aiQuestion, context_phase: currentPhase }),
      });
      const d = await res.json();
      setAiAnswer(d.answer || "Sorry, I couldn't find an answer.");
    } catch {
      setAiAnswer("Unable to reach AI assistant right now.");
    }
    setAiAsking(false);
  };

  const resetPanel = () => {
    setSelectedArticle(null);
    setSearchQuery("");
    setSearchResults(null);
    setAiAnswer("");
    setAiQuestion("");
  };

  if (!user) return null;

  return (
    <Sheet open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetPanel(); }}>
      <SheetTrigger asChild>
        <button
          className="fixed bottom-6 right-6 z-50 w-12 h-12 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white shadow-lg shadow-emerald-900/30 flex items-center justify-center transition-all hover:scale-105 active:scale-95"
          data-testid="help-floating-btn"
          aria-label="Help"
        >
          <HelpCircle className="w-5 h-5" />
        </button>
      </SheetTrigger>

      <SheetContent className="w-full sm:w-[420px] bg-zinc-950 border-zinc-800 p-0 flex flex-col" data-testid="help-panel">
        <SheetHeader className="p-4 pb-3 border-b border-zinc-800 flex-shrink-0">
          <div className="flex items-center justify-between">
            <SheetTitle className="text-zinc-100 flex items-center gap-2">
              {selectedArticle ? (
                <button onClick={() => setSelectedArticle(null)} className="flex items-center gap-1 text-sm text-zinc-400 hover:text-zinc-200">
                  <ArrowLeft className="w-4 h-4" /> Back
                </button>
              ) : (
                <>
                  <BookOpen className="w-4 h-4 text-emerald-400" />
                  <span className="text-sm font-semibold">Help</span>
                </>
              )}
            </SheetTitle>
            <Button variant="ghost" size="sm" onClick={() => { setOpen(false); navigate("/help"); }} className="text-xs text-zinc-400 hover:text-zinc-200 gap-1">
              Full page <ExternalLink className="w-3 h-3" />
            </Button>
          </div>
          {!selectedArticle && (
            <div className="relative mt-2">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
              <Input
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search..."
                className="pl-8 h-8 text-xs bg-zinc-900 border-zinc-700"
                data-testid="help-panel-search"
              />
            </div>
          )}
        </SheetHeader>

        <ScrollArea className="flex-1 min-h-0">
          <div className="p-4">
            {selectedArticle ? (
              /* Article Detail */
              <div data-testid="help-panel-article">
                <h2 className="text-base font-semibold mb-2">{selectedArticle.title}</h2>
                <p className="text-xs text-zinc-400 mb-4">{selectedArticle.summary}</p>
                <div className="prose prose-invert prose-xs max-w-none
                  prose-headings:font-semibold prose-h3:text-sm prose-h3:mt-5 prose-h3:mb-2
                  prose-p:text-zinc-300 prose-p:text-xs prose-p:leading-relaxed
                  prose-li:text-zinc-300 prose-li:text-xs
                  prose-strong:text-zinc-100
                  prose-table:text-xs
                  prose-th:text-left prose-th:p-1.5 prose-th:bg-zinc-900 prose-th:border-zinc-700
                  prose-td:p-1.5 prose-td:border-zinc-800
                ">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{selectedArticle.content}</ReactMarkdown>
                </div>
              </div>
            ) : searchResults !== null ? (
              /* Search Results */
              <div data-testid="help-panel-search-results">
                <p className="text-xs text-zinc-500 mb-3">{searchResults.length} result{searchResults.length !== 1 && "s"}</p>
                <div className="space-y-2">
                  {searchResults.map(a => (
                    <button key={a.slug} onClick={() => loadArticle(a.slug)} className="w-full text-left p-2.5 rounded-md bg-zinc-900/40 border border-zinc-800/60 hover:border-zinc-600 transition-all group">
                      <h4 className="text-xs font-medium group-hover:text-emerald-400 transition-colors">{a.title}</h4>
                      <p className="text-xs text-zinc-500 mt-0.5 line-clamp-1">{a.summary}</p>
                    </button>
                  ))}
                  {searchResults.length === 0 && <p className="text-xs text-zinc-500 text-center py-6">No results. Try asking AI below.</p>}
                </div>
              </div>
            ) : (
              /* Default: Contextual + Popular */
              <div data-testid="help-panel-home">
                {/* Contextual articles for current phase */}
                {contextArticles.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-2">
                      Relevant to {currentPhase} phase
                    </h3>
                    <div className="space-y-1.5">
                      {contextArticles.map(a => (
                        <button key={a.slug} onClick={() => loadArticle(a.slug)} className="w-full text-left p-2.5 rounded-md hover:bg-zinc-900/80 transition-all group flex items-center justify-between">
                          <div>
                            <h4 className="text-xs font-medium group-hover:text-emerald-400 transition-colors">{a.title}</h4>
                            <p className="text-xs text-zinc-500 mt-0.5 line-clamp-1">{a.summary}</p>
                          </div>
                          <ChevronRight className="w-3 h-3 text-zinc-600 flex-shrink-0 ml-2" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Quick Links */}
                <div className="mb-6">
                  <h3 className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-2">Popular Topics</h3>
                  <div className="space-y-1.5">
                    {[
                      { slug: "how-ai-credits-work", title: "How AI Credits Work" },
                      { slug: "how-dmaic-works", title: "How DMAIC Works" },
                      { slug: "quick-start-guide", title: "Quick Start Guide" },
                      { slug: "ai-credit-cost-breakdown", title: "Credit Cost Breakdown" },
                      { slug: "frequently-asked-questions", title: "FAQs" },
                    ].map(link => (
                      <button key={link.slug} onClick={() => loadArticle(link.slug)} className="w-full text-left p-2 rounded-md hover:bg-zinc-900/80 transition-all group flex items-center justify-between">
                        <span className="text-xs font-medium group-hover:text-emerald-400 transition-colors">{link.title}</span>
                        <ChevronRight className="w-3 h-3 text-zinc-600" />
                      </button>
                    ))}
                  </div>
                </div>

                {/* Ask AI */}
                <div className="p-3 rounded-lg bg-zinc-900/60 border border-zinc-800" data-testid="help-panel-ask-ai">
                  <div className="flex items-center gap-1.5 mb-2">
                    <Zap className="w-3.5 h-3.5 text-amber-400" />
                    <span className="text-xs font-semibold">Ask AI</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-900/40 text-emerald-400">Free</span>
                  </div>
                  <div className="flex gap-1.5">
                    <Input
                      value={aiQuestion}
                      onChange={e => setAiQuestion(e.target.value)}
                      onKeyDown={e => e.key === "Enter" && askAi()}
                      placeholder="Ask anything..."
                      className="h-7 text-xs bg-zinc-800 border-zinc-700 flex-1"
                      data-testid="help-panel-ai-input"
                    />
                    <Button onClick={askAi} disabled={aiAsking || !aiQuestion.trim()} size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 px-3">
                      {aiAsking ? <Loader2 className="w-3 h-3 animate-spin" /> : "Ask"}
                    </Button>
                  </div>
                  {aiAnswer && (
                    <div className="mt-2 p-2 rounded bg-zinc-800/60 border border-zinc-700" data-testid="help-panel-ai-answer">
                      <div className="prose prose-invert prose-xs max-w-none prose-p:text-zinc-300 prose-p:text-xs">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{aiAnswer}</ReactMarkdown>
                      </div>
                    </div>
                  )}
                </div>

                {/* Full help link */}
                <div className="mt-4 text-center">
                  <Button variant="ghost" size="sm" onClick={() => { setOpen(false); navigate("/help"); }} className="text-xs text-zinc-400 gap-1">
                    Browse all articles <ExternalLink className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
